<template>
  <div :class="parentClass">
    <label v-if="label" :for="id">{{ label }}</label>
    <validation-provider :rules="rules" v-slot="{ errors }" :immediate="true">
      <div class="input-wrapper" :class="input_wrapper_class">
        <div class="input-container">
          <input
            :type="inputType"
            ref="input"
            class="form-control"
            :class="{ 'is-invalid': errors && errors.length, 'form-control-sm': small }"
            :id="id"
            :placeholder="placeholder"
            :value="value"
            @input="updateValue()"
            @change="handleChange()"
            :disabled="disabled"
            :autocomplete="autocomplete"
            v-on:keypress="limitKeypress(value, maxval)"
          />
          <button
            type="button"
            class="eye-btn"
            :class="{ 'eye-btn-sm': small }"
            @click="toggleVisibility"
            :title="isHidden ? 'Show' : 'Hide'"
            tabindex="-1"
          >
            <i :class="isHidden ? 'fal fa-eye' : 'fal fa-eye-slash'"></i>
          </button>
        </div>
        <div class="error pt-1" v-if="errors && errors.length && !hide_error">
          <span class="text-danger text-xs" v-for="(error, index) in errors" :key="index">{{ error }}</span>
        </div>
      </div>
    </validation-provider>
  </div>
</template>

<script>
export default {
  name: "MaskedTextBox",
  props: {
    id: {
      type: String,
      required: true
    },
    label: {
      type: String
    },
    value: {
      type: [String, Number]
    },
    placeholder: {
      type: String
    },
    rules: {
      type: String
    },
    maxval: {
      type: Number
    },
    wrapper_class: {
      type: String
    },
    input_wrapper_class: {
      type: String
    },
    disabled: {
      type: Boolean,
      default: false
    },
    small: {
      type: Boolean,
      default: false
    },
    hide_error: {
      type: Boolean,
      default: false
    },
    derived: {
      type: Boolean,
      default: false
    },
    autocomplete: {
      type: String,
      default: "new-password"
    },
    maskOnSave: {
      type: [Boolean, Number, String],
      default: null
    }
  },
  data() {
    return {
      isHidden: false,
      hasUserManuallyToggled: false
    };
  },
  computed: {
    inputType() {
      return this.isHidden ? "password" : "text";
    },
    parentClass() {
      let classObj = { "form-group": !this.derived };
      if (this.wrapper_class) {
        classObj[this.wrapper_class] = true;
      }
      return classObj;
    }
  },
  watch: {
    value: {
      immediate: true,
      handler(newVal, oldVal) {
        const hasVal = newVal !== null && newVal !== undefined && String(newVal).trim() !== "";
        // If initial load or value set when user has not manually toggled:
        if (oldVal === undefined) {
          // Initial mount: if filled, hide; if empty, show
          this.isHidden = hasVal;
        } else if (!oldVal && hasVal && !this.hasUserManuallyToggled) {
          // Loaded asynchronously from server API
          this.isHidden = true;
        }
      }
    },
    maskOnSave() {
      // Whenever save trigger changes, re-mask if it has a value
      const hasVal = this.value !== null && this.value !== undefined && String(this.value).trim() !== "";
      if (hasVal) {
        this.isHidden = true;
        this.hasUserManuallyToggled = false;
      }
    }
  },
  methods: {
    toggleVisibility() {
      this.isHidden = !this.isHidden;
      this.hasUserManuallyToggled = true;
    },
    updateValue() {
      this.$emit("input", this.$refs.input.value);
    },
    handleChange() {
      this.$emit("change", this.$refs.input.value);
    },
    limitKeypress(value, maxLength) {
      if (maxLength && value != undefined && value.toString().length >= maxLength) {
        event.preventDefault();
      }
    },
    mask() {
      const hasVal = this.value !== null && this.value !== undefined && String(this.value).trim() !== "";
      if (hasVal) {
        this.isHidden = true;
        this.hasUserManuallyToggled = false;
      }
    }
  }
};
</script>

<style scoped lang="scss">
/* Chrome, Safari, Edge, Opera */
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Firefox */
input[type="number"] {
  -moz-appearance: textfield;
}

input[type="password"] {
  letter-spacing: 1px;
}

input {
  border-radius: toRem(5px);
  border-width: toRem(1.5px);
}

.input-wrapper {
  position: relative;
  padding-bottom: toRem(25px);

  .error {
    position: absolute;
    bottom: 0;
    left: 0;
  }
}

.input-container {
  position: relative;
  display: flex;
  align-items: center;

  .form-control {
    padding-right: toRem(38px);
  }

  .eye-btn {
    position: absolute;
    right: toRem(8px);
    top: 50%;
    transform: translateY(-50%);
    background: transparent;
    border: none;
    padding: 0 toRem(6px);
    cursor: pointer;
    color: #6c757d;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: toRem(16px);
    outline: none;
    transition: color 0.15s ease-in-out;
    z-index: 4;

    &:hover {
      color: #343a40;
    }

    &:focus {
      outline: none;
    }

    &.eye-btn-sm {
      font-size: toRem(13px);
      right: toRem(6px);
    }
  }
}
</style>
